<template>
  <div class="login-card-content reservation-code">
    <LoginCardHeading
      title="Código de reserva"
      description="Digite o código de reserva que você gerou em sua área de Acesso seguro."
    />

    <form
      class="reservation-code__form form"
      @submit.prevent="handleSubmit"
    >
      <BaseInput
        id="reservationCode"
        name="reservationCode"
        label="Código de reserva de 10 dígitos*"
        type="text"
        inputmode="numeric"
        autocomplete="off"
        maxlength="11"
        v-mask="'XXXXX-XXXXX'"
        v-model="reservationCode"
        :has-error="reservationCodeValidationsFailed"
        :error-message="setErrorMessage($v.reservationCode, fieldValidations) || getErrorMessageByField('2fa_recovery_code')"
        @input="resetServerValidations"
        @keypress="preventAlphaChars"
        @keypress.enter="handleSubmit"
        @focus="handleFocus"
        @blur="resetServerValidations"
      />

      <div class="reservation-code__form-actions">
        <h2>
          <button
            name="Acessar o Melhor Envio"
            type="submit"
            :class="getSubmitButtonClasses"
            :disabled="!reservationCode || invalidField($v.reservationCode)"
          >
            Confirmar
          </button>
        </h2>

        <h3 class="font-lato">
          <a
            href="https://menv.io/codigo-reserva-ajuda"
            target="_blank"
            rel="noopener noreferrer"
            title="Ir para a central de ajuda"
          >
            Precisa de ajuda?
          </a>
        </h3>
      </div>
    </form>

    <LoginCardDisclaimer
      text="Recuperou o acesso ao celular ou e-mail?"
      link-text="Digite o código de 6 dígitos"
      @handle-disclaimer="$emit('step-changed', getSteps.SECOND)"
    />
  </div>
</template>

<script>
// DEPENDENCIES
import { mask } from 'vue-the-mask'
import {
  required,
  minLength,
  helpers
} from "vuelidate/lib/validators";

// UTILS
import {nonDigit} from 'commons/js/RegExp';
import {preventAlphaChars} from "commons/js/utils/typing";
import recaptcha from "commons/js/ReCAPTCHAv3";

// MIXINS
import formValidator from "commons/js/mixins/formValidator";
import serverValidator from "commons/js/mixins/serverValidator";

// COMPONENTS
import BaseInput from 'commons/js/components/atoms/BaseInput';
import LoginCardHeading from "@home/js/components/login/LoginCardHeading";
import LoginCardDisclaimer from "@home/js/components/login/LoginCardDisclaimer";

// CUSTOM VALIDATIONS
const reservationCode = () => helpers.regex("reservationCodePattern", /^\d{5}-\d{5}$/);

export default {
  name: "ReservationCode",

  inject: [
    "user",
    "getSteps",
    "remember"
  ],

  directives: {
    mask
  },

  mixins: [
    formValidator,
    serverValidator
  ],

  components: {
    BaseInput,
    LoginCardHeading,
    LoginCardDisclaimer
  },

  data() {
    return {
      reservationCode: "",
      fieldValidations: [
        'required',
        'minLength',
        'alphaNum'
      ]
    }
  },

  validations: {
    reservationCode: {
      required,
      alphaNum: reservationCode(),
      minLength: minLength(11) // Must include the '-' character provided by v-mask.
    }
  },

  computed: {
    /**
     * Gets the properly classes to be applied on the submit button.
     *
     * @returns {array}
     */
    getSubmitButtonClasses() {
      return [
        "button-new",
        "text-small",
        this.invalidField(this.$v.reservationCode) ? "button-new--disabled" : "button-new--primary"
      ]
    },

    /**
     * Gets the reservationCode field validations, considering both front-end and back-end validations.
     *
     * @returns {boolean} Whether the reservationCode field is valid or not.
     */
    reservationCodeValidationsFailed() {
      return (
        this.invalidField(this.$v.reservationCode) ||
        this.hasInvalidFieldsOnServer
      );
    }
  },

  methods: {
    preventAlphaChars,

    /**
     * Handles the login using a 2FA recovery code.
     *
     * @returns {void}
     */
    async handleSubmit() {
      this.$v.$touch();

      if (!this.validForm) {
        return;
      }

      this.$emit('start-loading');
      this.isTyping = false;

      recaptcha('login_reservation_code', async (token) => {
        try {
          const recoveryCode = this.reservationCode.replace(nonDigit(), '');

          const payload = {
            username: this.user().name,
            password: this.user().password,
            token: token,
            remember: this.remember(),
            '2fa_recovery_code': recoveryCode
          };

          const {data} = await axios.post('/login', payload);

          this.$emit('handle-redirect', data);
        } catch(error) {
          this.handleError(error);
        } finally {
          this.$emit('finish-loading');
        }
      });
    }
  },
}
</script>

<style lang="stylus" scoped>
.reservation-code

  &__form
    width 100%
    margin-top 40px
    margin-bottom 40px

    @media screen and (min-width: 940px)
      margin-top 48px
      margin-bottom 74px

    .formLine
      min-height 56px
      padding-top 0

  &__form-actions
    display flex
    flex-direction column
    gap 16px
    align-items center
    margin-top 32px
    font-size 14px
    line-height 20px
</style>
